import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { forgotPassword, resetPassword, errorMessage } from '../services/api';
import { KernelPasswordRules } from '../components/KernelPasswordField';
import KernelCapsLockWarning from '../components/KernelCapsLockWarning';
import { isPasswordValid } from '../utils/kernelPassword';
import { useLanguage } from '../i18n/LanguageContext';
import '../components/kernelForgot.css';

/**
 * Fabbrica della pagina "password dimenticata" condivisa (rotta /forgot-password, registrata da createApp).
 * Riceve lo stesso `brand` e `showTitle` della pagina di login, così le due schermate hanno lo stesso aspetto.
 * Passo 1: si inserisce l'email. Passo 2: si inserisce il codice a 6 cifre ricevuto (o si arriva dal link
 * della mail, con email e token già nell'URL) e la nuova password.
 */
export function createForgotPasswordPage({ brand, showTitle = true }) {
  function ForgotPassword() {
    const { t } = useLanguage();
    const [searchParams] = useSearchParams();
    const linkToken = searchParams.get('token') || '';
    const linkEmail = searchParams.get('email') || '';
    const isLinkMode = linkToken !== '' && linkEmail !== '';

    const [step, setStep] = useState(isLinkMode ? 2 : 1);
    const [email, setEmail] = useState(linkEmail);
    const [otp, setOtp] = useState(linkToken);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    const handleRequest = async (e) => {
      e.preventDefault();
      setError('');
      setLoading(true);
      try {
        await forgotPassword(email.trim());
        setStep(2);
      } catch (err) {
        setError(errorMessage(err, t('forgot.errorGeneric')));
      } finally {
        setLoading(false);
      }
    };

    const handleReset = async (e) => {
      e.preventDefault();
      setError('');
      if (!isLinkMode && !/^\d{6}$/.test(otp.trim())) {
        setError(t('forgot.errorOtpDigits'));
        return;
      }
      if (!isPasswordValid(newPassword)) {
        setError(t('profile.passwordInvalid'));
        return;
      }
      if (newPassword !== confirmPassword) {
        setError(t('forgot.errorPasswordMismatch'));
        return;
      }
      setLoading(true);
      try {
        const response = await resetPassword(email.trim(), otp.trim(), newPassword, confirmPassword);
        setSuccess(response.message || t('forgot.success'));
      } catch (err) {
        setError(errorMessage(err, t('forgot.errorReset')));
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-logo">
            {brand.mode === 'plainImage' ? (
              <img src={brand.src} alt={t('auth.title')} className="login-logo-img" />
            ) : brand.mode === 'image' ? (
              <span className="brand-logo">
                <img src={brand.src} alt={t('auth.title')} />
              </span>
            ) : (
              <span className="brand-mark">{brand.mark}</span>
            )}
            {showTitle && <h1>{t('auth.title')}</h1>}
          </div>

          {success && (
            <div className="kf-success">
              <p>{success}</p>
              <Link to="/login" className="btn btn-lg">{t('forgot.backToLogin')}</Link>
            </div>
          )}

          {!success && step === 1 && (
            <>
              <h2 className="kf-title">{t('forgot.title')}</h2>
              <p className="kf-hint">{t('forgot.step1Hint')}</p>
              {error && <div className="error-message">{error}</div>}
              <form onSubmit={handleRequest}>
                <div className="form-group">
                  <label htmlFor="forgot-email">{t('forgot.email')}</label>
                  <input
                    type="email"
                    id="forgot-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('forgot.email')}
                    autoComplete="email"
                    required
                  />
                </div>
                <button type="submit" className="btn btn-lg" disabled={loading}>
                  {loading ? t('forgot.submitting') : t('forgot.submit')}
                </button>
              </form>
            </>
          )}

          {!success && step === 2 && (
            <>
              <h2 className="kf-title">{t('forgot.step2Title')}</h2>
              <p className="kf-hint">{isLinkMode ? t('forgot.step2HintLink', { email }) : t('forgot.step2Hint', { email })}</p>
              {error && <div className="error-message">{error}</div>}
              <form onSubmit={handleReset}>
                {!isLinkMode && (
                  <div className="form-group">
                    <label htmlFor="reset-otp">{t('forgot.otpLabel')}</label>
                    <input
                      type="text"
                      id="reset-otp"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                      placeholder={t('forgot.otpPlaceholder')}
                      maxLength={6}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      required
                    />
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="reset-password">{t('forgot.newPassword')}</label>
                  <input
                    type="password"
                    id="reset-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('forgot.newPassword')}
                    autoComplete="new-password"
                    required
                  />
                  <KernelCapsLockWarning />
                </div>
                <KernelPasswordRules value={newPassword} />
                <div className="form-group">
                  <label htmlFor="reset-confirm-password">{t('forgot.confirmPassword')}</label>
                  <input
                    type="password"
                    id="reset-confirm-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('forgot.confirmPassword')}
                    autoComplete="new-password"
                    required
                  />
                  <KernelCapsLockWarning />
                </div>
                <button type="submit" className="btn btn-lg" disabled={loading}>
                  {loading ? t('forgot.saving') : t('forgot.changePassword')}
                </button>
              </form>
            </>
          )}

          {!success && (
            <div className="kf-footer">
              <Link to="/login" className="kf-back">
                <span aria-hidden="true">←</span> {t('forgot.backToLogin')}
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  }
  return ForgotPassword;
}
